import { useQuery } from '@tanstack/react-query'

import type { UploadPolicy } from '../shared/models/UploadPolicy.ts'

export function resolveMediaFormat(name: string, type?: string | null, policy?: UploadPolicy) {
  const extensionIndex = name.lastIndexOf('.')
  if (extensionIndex <= 0) {
    return null
  }
  const extension = name.slice(extensionIndex + 1).toLowerCase()
  const declaredType = type?.split(';')[0].trim().toLowerCase() ?? ''
  const mediaType = declaredType && declaredType !== 'application/octet-stream'
    ? declaredType
    : mimeFromExtension(extension, policy)
  const kind = getMediaKind(mediaType)
  return kind ? { extension, mediaType, kind } : null
}

function getMediaKind(mediaType: string): 'image' | 'video' | 'audio' | null {
  if (mediaType.startsWith('image/')) {
    return 'image'
  }
  if (mediaType.startsWith('video/')) {
    return 'video'
  }
  if (mediaType.startsWith('audio/')) {
    return 'audio'
  }
  return null
}

export function getChunkSize(policy?: UploadPolicy): number {
  return policy?.maxFileSizeBytes ?? 0
}

export function resolveUploadFormat(name: string, type: string | null | undefined, policy: UploadPolicy) {
  const extensionIndex = name.lastIndexOf('.')
  if (extensionIndex <= 0) {
    return null
  }
  const extension = name.slice(extensionIndex + 1).toLowerCase()
  const format = policy.formats.find((candidate) => candidate.extension.toLowerCase() === extension)
  if (!format) {
    return null
  }

  const declaredType = type?.split(';')[0].trim().toLowerCase() ?? ''
  const acceptedTypes = [format.mimeType]
  if (declaredType && declaredType !== 'application/octet-stream' && !acceptedTypes.includes(declaredType)) {
    return null
  }

  const mediaType = declaredType && declaredType !== 'application/octet-stream'
    ? declaredType
    : format.mimeType
  const kind = getMediaKind(mediaType)
  return kind ? { extension, mediaType, kind } : null
}

export function getUploadAccept(policy: UploadPolicy | undefined): string {
  if (!policy) {
    return ''
  }

  return [...new Set(policy.formats.flatMap((format) => [
    `.${format.extension}`,
    // format.mimeType,
    // ...(format.alternateMimeTypes ?? []),
  ]))].join(',')
}



/** A single file in the upload list. */
export interface WorkspaceFileEntry {
  /** React key and removal identifier. */
  id: string
  name: string
  size?: number
  /** MIME type, e.g. "image/jpeg". */
  type: string
  /** Present only for user-selected files. */
  file?: File
  /** base64 data URL for image previews; null for non-images. */
  previewUrl: string | null
  /** "default" = pre-loaded from server; "ready" = user-selected, awaiting submit. */
  status: 'default' | 'ready'
}

const getExtension = (name: string): string => name.split('.').pop()?.toLowerCase() ?? ''



const mimeFromExtension = (extension: string, policy?: UploadPolicy): string => {
  if (policy) {
    return policy.formats.find((format) => format.extension.toLowerCase() === extension)?.mimeType ??
      'application/octet-stream'
  }

  return 'application/octet-stream'
}

export const generateId = (): string => Math.random().toString(36).slice(2, 9)

/** Converts a byte count to a human-readable string, e.g. "1.5 KB". */
export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

/** Returns true if the file should be treated as an image (by MIME type or extension). */
export const isImageFile = (file: Pick<WorkspaceFileEntry, 'name' | 'type'>): boolean => {
   return file.type.startsWith('image/')

  // return IMAGE_EXTENSIONS.includes(getExtension(file.name) as (typeof IMAGE_EXTENSIONS)[number])
}

/** Reads a File as a base64 data URL. */
export const readFileAsDataURL = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => resolve(e.target?.result as string)
    reader.onerror = () => reject(new Error('FileReader failed to read file'))
    reader.readAsDataURL(file)
  })

/**
 * Fetches an image URL and converts it to a FileEntry with a base64 previewUrl.
 */
export const imagePathToFileEntry = async (
  url: string,
  overrides: Partial<WorkspaceFileEntry> = {},
): Promise<WorkspaceFileEntry> => {
  const rawName = url.split('/').pop()?.split('?')[0] ?? 'image'
  const ext = getExtension(rawName)
  const inferredType = mimeFromExtension(ext)

  const response = await fetch(url, { cache: 'force-cache' })
  if (!response.ok) {
    throw new Error(`imagePathToFileEntry: failed to fetch "${url}" (${response.status})`)
  }

  const blob = await response.blob()
  const file = new File([blob], rawName, { type: blob.type || inferredType })
  const previewUrl = await readFileAsDataURL(file)

  return {
    id: generateId(),
    name: rawName,
    size: blob.size,
    type: file.type,
    file,
    previewUrl,
    status: 'default',
    ...overrides,
  }
}

export function useImagePathToFileEntry(
  url: string | null | undefined,
  overrides: Partial<WorkspaceFileEntry> = {},
) {
  return useQuery({
    queryKey: ['imagePathToFileEntry', url],
    queryFn: () => imagePathToFileEntry(url as string, overrides),
    enabled: !!url,
  })
}

/**
 * Validates a batch of files against upload constraints.
 * Checks: type, per-file size, max count, total size.
 */
export const validateFiles = (
  incoming: File[],
  currentFiles: WorkspaceFileEntry[],
  accept: string[],
  maxSizeMB: number,
  maxFiles: number,
  maxTotalMB: number | null,
): { valid: File[]; errs: string[] } => {
  const errs: string[] = []
  const maxBytes = maxSizeMB * 1024 * 1024
  const typeAccepted = accept.length === 0
    ? incoming
    : incoming.filter((file) => {
        const extension = `.${getExtension(file.name)}`
        return accept.some(
          (pattern) =>
            pattern === extension ||
            pattern === file.type ||
            (pattern.endsWith('/*') && file.type.startsWith(pattern.replace('/*', '/'))),
        )
      })

  const typeRejected = incoming.filter((f) => !typeAccepted.includes(f))
  if (typeRejected.length) {
    errs.push(`${typeRejected.map((f) => f.name).join(', ')} — unsupported file type`)
  }

  const sizeRejected = typeAccepted.filter((f) => f.size > maxBytes)
  if (sizeRejected.length) {
    errs.push(`${sizeRejected.map((f) => f.name).join(', ')} — exceeds ${maxSizeMB} MB limit`)
  }

  let valid = typeAccepted.filter((f) => f.size <= maxBytes)

  const availableSlots = maxFiles - currentFiles.length
  if (valid.length > availableSlots) {
    errs.push(`Maximum ${maxFiles} file${maxFiles === 1 ? '' : 's'} allowed`)
    valid = valid.slice(0, availableSlots)
  }

  if (maxTotalMB !== null) {
    const existingBytes = currentFiles.reduce((sum, f) => sum + (f.size ?? 0), 0)
    const incomingBytes = valid.reduce((sum, f) => sum + f.size, 0)
    if (existingBytes + incomingBytes > maxTotalMB * 1024 * 1024) {
      errs.push(`Total upload size would exceed ${maxTotalMB} MB`)
      valid = []
    }
  }

  return { valid, errs }
}

/** Sanitizes a filename by lowercasing, replacing non-alphanumerics with underscores, and trimming underscores. */
// export const sanitizeFilename = (value: string): string => {
//   return value
//     .trim()
//     .toLowerCase()
//     .replace(/[^a-z0-9]+/g, '_')
//     .replace(/^_+|_+$/g, '')
// }

/** Extracts a filename from a Content-Disposition header, handling both standard and RFC5987 formats. */
// export const getFilenameFromDisposition = (contentDisposition?: string | null): string | null => {
//   if (!contentDisposition) return null
//   // RFC5987 format: filename*=UTF-8''file.zip
//   const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;\n]+)/i)
//   if (utf8Match?.[1]) {
//     return decodeURIComponent(utf8Match[1])
//   }
//   // Standard format: filename="file.zip"
//   const standardMatch = contentDisposition.match(/filename="?([^"]+)"?/i)
//   if (standardMatch?.[1]) {
//     return standardMatch[1]
//   }

//   return null
// }

/** Triggers a browser file download for a Blob. */
// export const downloadBlob = (blob: Blob, filename: string): void => {
//   const url = URL.createObjectURL(blob)
//   try {
//     const link = document.createElement('a')
//     link.href = url
//     link.download = filename
//     link.style.display = 'none'
//     document.body.appendChild(link)
//     link.click()
//     link.remove()
//   } finally {
//     URL.revokeObjectURL(url)
//   }
// }
